import { normalize } from 'normalizrplus';
import * as AuthMutations from '@/store/mutations/Auth.mutations';
import * as WorkspaceActions from '@/store/actions/Workspace.actions';
import * as WorkspaceMutations from '@/store/mutations/Workspace.mutations';
import * as UserMutations from '@/store/mutations/User.mutations';
import * as DatabaseService from '@/services/Database.service';
import * as api from '@/services/Api.service';
import { dictionaryFromDocumentArray } from '@/helpers/dataHelpers';
import * as schemas from '@/store/schemas';


function receiveWorkspaces(state, payload) {
  state.workspaces = {
    ...state.workspaces,
    ...payload.workspaces,
  };
  state.isWorkspaceLoading = false;
}

const getInitialState = () => ({
  workspaces: {},
  workspaceIds: [],
  activeWorkspaceId: null,
  isWorkspaceLoading: false,
  workspaceError: null,
});

const state = getInitialState();

const getters = {
  activeWorkspace(state) {
    return (
      (state.activeWorkspaceId && state.workspaces[state.activeWorkspaceId])
        ? { ...state.workspaces[state.activeWorkspaceId] }
        : null
    );
  },
};

const mutations = {
  [WorkspaceMutations.WORKSPACE_REQUEST_DATA](state) {
    state.isWorkspaceLoading = true;
  },
  [WorkspaceMutations.WORKSPACE_BEGIN_WRITE_DATA](state) {
    state.isWorkspaceLoading = true;
  },
  [WorkspaceMutations.WORKSPACE_COMPLETE_WRITE_DATA](state) {
    state.isWorkspaceLoading = false;
  },
  [WorkspaceMutations.RECEIVE_WORKSPACES]: receiveWorkspaces,
  [UserMutations.RECEIVE_USERS]: receiveWorkspaces,
  [WorkspaceMutations.SET_ACTIVE_WORKSPACE](state, payload) {
    state.activeWorkspaceId = payload.workspaceId;
  },
  [WorkspaceMutations.SET_WORKSPACE_USER_COUNT](state, payload) {
    state.workspaces[state.activeWorkspaceId] = {
      ...state.workspaces[state.activeWorkspaceId],
      userCount: payload.userCount,
    };
  },
  [WorkspaceMutations.SET_WORKSPACE_ERROR](state, payload) {
    state.isWorkspaceLoading = false;
    state.workspaceError = payload;
  },
  [AuthMutations.LOG_OUT](state) {
    Object.assign(state, getInitialState());
  },
};

const actions = {
  // This is only to find multiple workspaces for which a user is the OWNER (not just a member).
  async [WorkspaceActions.FETCH_USER_WORKSPACES](context, payload) {
    context.commit(WorkspaceMutations.WORKSPACE_REQUEST_DATA);
    return new Promise(async (resolve, reject) => {
      try {
        const { docs: userWorkspaces } = await DatabaseService.getUserWorkspaces(payload.userId);
        const shapedUserWorkspaces = dictionaryFromDocumentArray(userWorkspaces);

        context.commit(WorkspaceMutations.RECEIVE_WORKSPACES, { workspaces: shapedUserWorkspaces });
        resolve(Object.keys(shapedUserWorkspaces));
      } catch (err) {
        reject();
        console.error(err); // eslint-disable-line no-console
      }
    });
  },
  async [WorkspaceActions.FETCH_WORKSPACE_BY_ID](context, payload) {
    context.commit(WorkspaceMutations.WORKSPACE_REQUEST_DATA);
    return new Promise(async (resolve, reject) => {
      try {
        const workspaceDoc = await DatabaseService.getWorkspaceById(payload.workspaceId);
        const shapedWorkspaces = dictionaryFromDocumentArray([workspaceDoc]);
        context.commit(WorkspaceMutations.RECEIVE_WORKSPACES, { workspaces: shapedWorkspaces });
        resolve();
      } catch (err) {
        // TODO -- handle error in state module
        console.error(err);
        reject(err);
      }
    });
  },
  async [WorkspaceActions.CREATE_WORKSPACE](context, payload) {
    context.commit(WorkspaceMutations.WORKSPACE_BEGIN_WRITE_DATA);
    try {
      const { data: { workspace } } = await api.workspace.create(payload);
      context.commit(WorkspaceMutations.WORKSPACE_COMPLETE_WRITE_DATA);

      const normalizedWorkspace = normalize(workspace, schemas.workspace);
      context.commit(WorkspaceMutations.RECEIVE_WORKSPACES, normalizedWorkspace.entities);

      context.commit(
        WorkspaceMutations.SET_ACTIVE_WORKSPACE,
        { workspaceId: context.getters.loggedInUser.workspaceId },
      );
    } catch (err) {
      console.error(err); // eslint-disable-line no-console
      context.commit(WorkspaceMutations.SET_WORKSPACE_ERROR);
    }
  },
  async [WorkspaceActions.UPDATE_WORKSPACE_BY_ID](context, payload) {
    context.commit(WorkspaceMutations.WORKSPACE_BEGIN_WRITE_DATA);
    try {
      await DatabaseService.updateWorkspaceById(payload.workspaceId, payload.updatedWorkspace);
      context.commit(WorkspaceMutations.WORKSPACE_COMPLETE_WRITE_DATA);
      await context.dispatch(WorkspaceActions.FETCH_WORKSPACE_BY_ID, { workspaceId: payload.workspaceId });
    } catch (err) {
      console.error(err); // eslint-disable-line no-console
    }
  },
};

export default {
  state,
  getters,
  mutations,
  actions,
};
